import { Fragment } from 'react';
import { useNavigate } from 'react-router';
import { useLocalize } from '@hooks/use-localize';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IScreeningProgressModel } from '@/types/screening/screening-progress';
import BackToDashboard from '@/components/chrome/back-to-dashboard';

interface IScreeningProgressProps {
    model: IScreeningProgressModel;
    overrideStep?: DashRegistrationStep
}

interface IProgressStep {
    step: DashRegistrationStep;
    isComplete: boolean;
    label: string;
    display: boolean;
}

interface IProgressSection {
    label: string;
    steps: IProgressStep[];
}

const StepIcon = ({ isComplete }: { isComplete: boolean }) =>
    isComplete ? (
        <i className="fas fa-check-circle progress-list__icon" aria-hidden="true"></i>
    ) : (
        <span className="progress-list__icon" aria-hidden="true"></span>
    );

const ScreeningProgress = ({ model, overrideStep }: IScreeningProgressProps) => {
    const t = useLocalize(model.languageData);
    const navigate = useNavigate();

    const quickStart: IProgressStep = {
        step: DashRegistrationStep.QuickStart,
        isComplete: model.isQuickStartComplete,
        label: t('DashLabel_QuickStart', 'Quick Start'),
        display: true
    };

    const screeningQuestions: IProgressStep[] = [
        {
            step: DashRegistrationStep.AcceptTransfusions,
            isComplete: model.isAcceptTransfusionsComplete,
            label: t('DashLabel_ProgressBloodTransfusions', 'Blood Transfusions'),
            display: true
        },
        {
            step: DashRegistrationStep.AssessLiverHealth,
            isComplete: model.isAssessLiverHealthComplete,
            label: t('DashLabel_ProgressLiverDisease', 'Liver Disease'),
            display: model.isLiver
        },
        {
            step: DashRegistrationStep.AssessNutcracker,
            isComplete: model.isAssessNutcrackerComplete,
            label: t('DashLabel_ProgressNutcracker', 'Nutcracker Syndrome'),
            display: !model.isLiver
        },
        {
            step: DashRegistrationStep.AssessOrganDisease,
            isComplete: model.isAssessOrganDiseaseComplete,
            label: t('DashLabel_ProgressKidneyDisease', 'Kidney Disease'),
            display: !model.isLiver
        },
        {
            step: DashRegistrationStep.AssessHeartAttack,
            isComplete: model.isAssessHeartAttackComplete,
            label: t('DashLabel_ProgressHeartAttack', 'Heart Attack'),
            display: true
        },
        {
            step: DashRegistrationStep.AssessDiabetes,
            isComplete: model.isAssessDiabetesComplete,
            label: t('DashLabel_ProgressDiabetes', 'Diabetes'),
            display: true
        },
        {
            step: DashRegistrationStep.AssessCancer,
            isComplete: model.isAssessCancerComplete,
            label: t('DashLabel_ProgressCancer', 'Cancer'),
            display: true
        },
        {
            step: DashRegistrationStep.AssessKidneyStones,
            isComplete: model.isAssessKidneyStonesComplete,
            label: t('DashLabel_ProgressKidneyStones', 'Kidney Stones'),
            display: !model.isLiver
        },
        {
            step: DashRegistrationStep.PregnantOrNursing,
            isComplete: model.isPregnantOrNursingComplete,
            label: t('DashLabel_ProgressPregnantorNursing', 'Pregnant or Nursing'),
            display: model.isFemale
        },
        {
            step: DashRegistrationStep.AssessTobacco,
            isComplete: model.isAssessTobaccoComplete,
            label: t('DashLabel_ProgressTobaccoUse', 'Tobacco Use'),
            display: true
        },
        {
            step: DashRegistrationStep.AssessHiv,
            isComplete: model.isAssessHivComplete,
            label: t('DashLabel_ProgressHIV', 'HIV'),
            display: true
        },
        {
            step: DashRegistrationStep.DonationDetailsMicrosite,
            isComplete: model.isDonationDetailsMicrositeComplete,
            label: t('DashLabel_ProgressDonationDetails', 'Donation Details'),
            display: model.hasMicrositeRecipient
        },
        {
            step: DashRegistrationStep.DonationDetails,
            isComplete: model.isDonationDetailsComplete,
            label: t('DashLabel_ProgressDonationDetails', 'Donation Details'),
            display: !model.hasMicrositeRecipient
        },
        {
            step: DashRegistrationStep.VoucherChoice,
            isComplete: model.isVoucherChoiceComplete,
            label: t('DashLabel_ProgressVoucherProgram', 'The Voucher Program'),
            display: !model.isLiver
        },
        {
            step: DashRegistrationStep.ScreeningSummary,
            isComplete: model.isScreeningSummaryComplete,
            label: t('DashLabel_ProgressScreeningSummary', 'Screening Summary'),
            display: true
        },
        {
            step: DashRegistrationStep.ScreeningComplete,
            isComplete: model.isScreeningComplete,
            label: t('DashLabel_ProgressScreeningComplete', 'Screening Complete'),
            display: true
        },
    ];

    // if (!model.isLiver) {
    //     screeningQuestions.push({
    //         step: DashRegistrationStep.AssessKidneyStones,
    //         isComplete: model.isAssessKidneyStonesComplete,
    //         label: t('DashLabel_ProgressKidneyStones', 'Kidney Stones'),
    //     });
    // }

    // if (model.isFemale) {
    //     screeningQuestions.push({
    //         step: DashRegistrationStep.PregnantOrNursing,
    //         isComplete: model.isPregnantOrNursingComplete,
    //         label: t('DashLabel_ProgressPregnantorNursing', 'Pregnant or Nursing'),
    //     });
    // }

    // screeningQuestions.push(
    //     {
    //         step: DashRegistrationStep.AssessTobacco,
    //         isComplete: model.isAssessTobaccoComplete,
    //         label: t('DashLabel_ProgressTobaccoUse', 'Tobacco Use'),
    //     },
    //     {
    //         step: DashRegistrationStep.AssessHiv,
    //         isComplete: model.isAssessHivComplete,
    //         label: t('DashLabel_ProgressHIV', 'HIV'),
    //     },
    // );

    // const remaining: IProgressStep[] = [
    //     model.hasMicrositeRecipient
    //         ? {
    //             step: DashRegistrationStep.DonationDetailsMicrosite,
    //             isComplete: model.isDonationDetailsMicrositeComplete,
    //             label: t('DashLabel_ProgressDonationDetails', 'Donation Details'),
    //             display: true
    //         }
    //         : {
    //             step: DashRegistrationStep.DonationDetails,
    //             isComplete: model.isDonationDetailsComplete,
    //             label: t('DashLabel_ProgressDonationDetails', 'Donation Details'),
    //             display: true
    //         },
    // ];

    // if (!model.isLiver) {
    //     remaining.push(
    //         // {
    //         //     step: DashRegistrationStep.VoucherProgram,
    //         //     isComplete: model.isVoucherProgramComplete,
    //         //     label: t('DashLabel_ProgressVoucherProgram', 'The Voucher Program'),
    //         // },
    //         {
    //             step: DashRegistrationStep.VoucherChoice,
    //             isComplete: model.isVoucherChoiceComplete,
    //             label: t('DashLabel_ProgressVoucherProgram', 'The Voucher Program'),
    //             display: true
    //         },
    //     );
    // }

    // remaining.push(
    //     {
    //         step: DashRegistrationStep.ScreeningSummary,
    //         isComplete: model.isScreeningSummaryComplete,
    //         label: t('DashLabel_ProgressScreeningSummary', 'Screening Summary'),
    //         display: true
    //     },
    //     {
    //         step: DashRegistrationStep.ScreeningComplete,
    //         isComplete: model.isScreeningComplete,
    //         label: t('DashLabel_ProgressScreeningComplete', 'Screening Complete'),
    //         display: true
    //     },
    // );

    const section: IProgressSection = {
        label: t('DashLabel_ProgressScreeningQuestions', 'Screening Questions'),
        steps: screeningQuestions,
    };

    const stepToCheck = overrideStep ? overrideStep : model.currentRegistrationStep;

    const renderStep = (item: IProgressStep, isChild: boolean) => {
        const isActive = stepToCheck === item.step;
        const isLink = item.step < model.currentRegistrationStep || item.isComplete;
        const linkStep = screeningLinkMap(model.isFemale, model.isLiver).find(x => x.step == item.step);
        const next = linkStep?.link;

        const className =
            'progress-list__item' +
            (isChild ? ' progress-list__item--child' : '') +
            (isActive
                ? ' progress-list__item--active'
                : item.isComplete
                    ? ' progress-list__item--complete'
                    : '');

        const content = (
            <>
                <StepIcon isComplete={item.isComplete && !isActive} />
                {item.label}
            </>
        );

        return isLink ? (
            <a key={item.step} className={className + ' clickable'} onClick={() => navigate(next ?? '')}>
                {content}
            </a>
        ) : (
            <span key={item.step} className={className}>
                {content}
            </span>
        );
    };

    const isSectionComplete = section.steps.every((x) => x.isComplete);
    const isSectionCurrent = section.steps.some((x) => x.step === stepToCheck);

    return (
        <div className="screening-progress">
            {model.isIE && (
                <div className="alert alert-warning">
                    {t(
                        'Alert_BrowserRecommend',
                        'To ensure proper function of this site, we recommend that you use Chrome or FireFox.',
                    )}
                    .
                </div>
            )}

            <h5 className="screening-progress__title" id="progress">
                {t('DashLabel_ScreeningQuestions', 'Screening Information')}:
            </h5>

            <div className="progress-list">
                {renderStep(quickStart, false)}

                <span
                    className={
                        'progress-list__item progress-list__item--section' +
                        (isSectionComplete || isSectionCurrent ? ' progress-list__item--complete' : '')
                    }
                >
                    <StepIcon isComplete={isSectionComplete} />
                    {section.label}
                </span>

                {section.steps.filter(x => x.display).map((item) => (
                    <Fragment key={item.step}>{renderStep(item, true)}</Fragment>
                ))}

                {/* {remaining.map((item) => (
                    <Fragment key={item.step}>{renderStep(item, false)}</Fragment>
                ))} */}
            </div>

            <div style={{ paddingTop: 20 }}>
                <BackToDashboard
                    link={null}
                    t={t}
                />
            </div>
        </div>
    );
};

export default ScreeningProgress;
